// apps/hyperseguros-web/src/components/Nav/DesktopMegaMenu.tsx

'use client'

import React from 'react'
import Link from 'next/link'
import { Box, useTheme } from '@mui/material'
import type { TNavMenu, TNavMenuLink } from './types'

type Props = {
  menu: TNavMenu
  onLinkClick: () => void
}

/** A flat list is split into columns of at most this many rows. */
const FLAT_MAX_ROWS = 8
/** Upper bound on columns for both layouts, so labels keep a readable width. */
const MAX_COLUMNS = 4
/**
 * Widest a group column gets. Menus with few groups keep their columns
 * side by side at the left instead of stretching across the whole panel.
 */
const GROUP_COLUMN_MAX_WIDTH = '240px'

/**
 * Mega-menu panel body. Renders the column grid for a single TNavMenu.
 *
 * Headed groups take one column each; a heading-less group among them
 * keeps an empty heading row so its links line up with its neighbours'. A
 * menu whose groups carry no headings at all is one flat list: its items
 * flow top-to-bottom into as many columns as needed to keep every column at
 * most `FLAT_MAX_ROWS` long.
 *
 * Positioning, glass treatment, and global-document listeners (ESC,
 * outside-click) are owned by the parent NavBar/orchestrator — this
 * component is purely the panel content.
 */
export default function DesktopMegaMenu({ menu, onLinkClick }: Props) {
  const theme = useTheme()
  // Same muted gray as Footer/index.tsx's `mutedColor`, not an opacity on
  // the inherited (unset) text color: this panel has no explicit color of
  // its own, so it inherits full-strength black/white (browsers resolve
  // canvastext off `color-scheme`), and opacity 0.6 on that only clears
  // ~4.75:1 against the panel's own resting composite (its 0.4/0.45-alpha
  // white/black tint over the page background) — a margin the panel's
  // varying real backdrop (photos, dark bands) routinely erases. The
  // literal clears 4.5:1 against both panel backgrounds in both modes
  // (~4.75:1 light, ~4.65:1 dark) independent of what is behind the glass.
  const eyebrowColor = theme.palette.mode === 'dark' ? '#86868b' : '#6e6e73'
  const isFlat = menu.groups.every((group) => !group.heading)
  if (isFlat) {
    return (
      <FlatColumns
        items={menu.groups.flatMap((group) => group.items)}
        onLinkClick={onLinkClick}
      />
    )
  }

  return (
    <Box
      sx={{
        px: { xs: '20px', md: '32px' },
        py: 3,
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.max(1, Math.min(menu.groups.length, MAX_COLUMNS))}, minmax(0, ${GROUP_COLUMN_MAX_WIDTH}))`,
        gap: 4,
      }}
    >
      {menu.groups.map((group, index) => (
        <Box key={group.heading ?? index}>
          <Box
            aria-hidden={group.heading ? undefined : true}
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: eyebrowColor,
              mb: 1,
              visibility: group.heading ? 'visible' : 'hidden',
            }}
          >
            {group.heading ?? '\u00a0'}
          </Box>
          {group.items.map((item) => (
            <MenuLink key={item.href} item={item} onLinkClick={onLinkClick} />
          ))}
        </Box>
      ))}
    </Box>
  )
}

function FlatColumns({
  items,
  onLinkClick,
}: {
  items: TNavMenuLink[]
  onLinkClick: () => void
}) {
  const columns = Math.max(
    1,
    Math.min(Math.ceil(items.length / FLAT_MAX_ROWS), MAX_COLUMNS)
  )
  const rows = Math.max(1, Math.ceil(items.length / columns))
  return (
    <Box
      sx={{
        px: { xs: '20px', md: '32px' },
        py: 3,
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, auto)`,
        gridAutoFlow: 'column',
        columnGap: 4,
      }}
    >
      {items.map((item) => (
        <MenuLink key={item.href} item={item} onLinkClick={onLinkClick} />
      ))}
    </Box>
  )
}

function MenuLink({
  item,
  onLinkClick,
}: {
  item: TNavMenuLink
  onLinkClick: () => void
}) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Link
        href={item.href}
        onClick={onLinkClick}
        style={{
          textDecoration: 'none',
          color: 'inherit',
          fontSize: '15px',
          lineHeight: 1.3,
        }}
      >
        {item.label}
      </Link>
    </Box>
  )
}
